import type { Variable } from "@/domains/templates/types";
import type {
  BoundInputDefinition,
  SubanalysisDefinitionInput,
} from "@/domains/templates/subanalysis.types";

export function invocationDefinition(
  variables: Variable[],
): SubanalysisDefinitionInput {
  const root = variables.find((v) => v.variable_role === "subanalysis_call");
  const source = variables.find(
    (v) => v.variable_role === "subanalysis_source",
  );
  if (!root || !source)
    throw new Error(
      "This section needs its child template and earlier section definitions. Reload the section.",
    );
  return {
    call: {
      binding_template_id: root.binding_template_id ?? "",
      binding_source_step_id: source.binding_source_step_id,
      binding_source_variable_id: source.binding_source_variable_id,
      binding_path: source.binding_path ?? "",
    },
    inputs: variables
      .filter((v) => v.variable_role === "bound_input")
      .map((v) => {
        const base = {
          binding_target_variable_id: v.binding_target_variable_id!,
        };
        switch (v.binding_kind) {
          case "constant":
            return {
              ...base,
              binding_kind: "constant",
              variable_default: v.variable_default,
            };
          case "item":
            return {
              ...base,
              binding_kind: "item",
              binding_path: v.binding_path ?? "",
            };
          case "parent_variable":
            return {
              ...base,
              binding_kind: "parent_variable",
              binding_source_variable_id: v.binding_source_variable_id!,
            };
          case "step_variable":
            return {
              ...base,
              binding_kind: "step_variable",
              binding_source_variable_id: v.binding_source_variable_id!,
              ...(v.binding_path ? { binding_path: v.binding_path } : {}),
            };
          case "target_default":
            return { ...base, binding_kind: "target_default" };
          default:
            return { ...base, binding_kind: "unbound" };
        }
      }),
  };
}
export function replaceInvocationBinding(
  definition: SubanalysisDefinitionInput,
  input: BoundInputDefinition,
): SubanalysisDefinitionInput {
  if (
    !definition.inputs.some(
      (row) =>
        row.binding_target_variable_id === input.binding_target_variable_id,
    )
  )
    throw new Error(
      "The child input changed. Reopen the variable to review its current definition.",
    );
  return {
    ...definition,
    inputs: definition.inputs.map((row) =>
      row.binding_target_variable_id === input.binding_target_variable_id
        ? input
        : row,
    ),
  };
}
export function reconcileInvocationSource(
  definition: SubanalysisDefinitionInput,
  stepId: string,
  variableId: string,
  path: string,
  allowedSourceIds: Set<string>,
): SubanalysisDefinitionInput {
  const changed =
    definition.call.binding_source_step_id !== (stepId || null) ||
    definition.call.binding_source_variable_id !== (variableId || null) ||
    definition.call.binding_path !== path;
  return {
    call: {
      ...definition.call,
      binding_source_step_id: stepId || null,
      binding_source_variable_id: variableId || null,
      binding_path: path,
    },
    inputs: definition.inputs.map((row) =>
      (row.binding_kind === "step_variable" &&
        !allowedSourceIds.has(row.binding_source_variable_id)) ||
      (row.binding_kind === "item" && changed)
        ? {
            binding_target_variable_id: row.binding_target_variable_id,
            binding_kind: "unbound",
          }
        : row,
    ),
  };
}
